import React, { useState } from "react";
import { connect } from "react-redux";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import DatePicker from "react-datepicker";
import WorkIcon from "@mui/icons-material/Work";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import "react-datepicker/dist/react-datepicker.css";

import { StoreState } from "../../redux/configure-store";
import { createEngagement } from "../admin-actions";
import { backgrounds } from "../../theme/theme";

const createInitialErrorState = () => ({
  clientName: "",
  shortDescription: "",
  description: "",
  startDate: "",
  hourlyRateUsd: "",
  numberOfHoursPerWeek: "",
  trackingType: "",
});

interface EngagementsCreateProps {
  user: any;
  createEngagement: (data: any) => void;
}

function EngagementsCreate(props: EngagementsCreateProps) {
  const { user, createEngagement } = props;

  const [data, setData] = useState({
    clientName: "",
    shortDescription: "",
    description: "",
    startDate: new Date(),
    hourlyRateUsd: "",
    numberOfHoursPerWeek: "",
    trackingType: "hourly-two-timesheets-per-month",
  });
  const [errors, setErrors] = useState(createInitialErrorState());

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setData((prev) => ({ ...prev, startDate: date }));
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = createInitialErrorState();

    const emptyValueErrorMessage = "This field is required";

    if (data.clientName.length === 0) {
      newErrors.clientName = emptyValueErrorMessage;
    }

    if (data.hourlyRateUsd.length === 0) {
      newErrors.hourlyRateUsd = emptyValueErrorMessage;
    }

    if (data.shortDescription.length === 0) {
      newErrors.shortDescription = emptyValueErrorMessage;
    }

    if (data.description.length === 0) {
      newErrors.description = emptyValueErrorMessage;
    }

    if (!data.startDate) {
      newErrors.startDate = emptyValueErrorMessage;
    }

    if (!data.numberOfHoursPerWeek) {
      newErrors.numberOfHoursPerWeek = emptyValueErrorMessage;
    }

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some(
      (error: string) => error.length !== 0
    );

    if (!hasErrors) {
      createEngagement({
        clientName: data.clientName,
        userId: user.id,
        shortDescription: data.shortDescription,
        description: data.description,
        startDate: data.startDate,
        hourlyRateUsd: parseFloat(data.hourlyRateUsd),
        numberOfHoursPerWeek: parseFloat(data.numberOfHoursPerWeek),
        trackingType: data.trackingType,
      });
    }
  };

  if (!user || !user.id) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">You must select a user first</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: backgrounds.page,
        py: 4,
        px: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          mx: "auto",
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: "primary.main",
              mb: 2,
            }}
          >
            <WorkIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Typography component="h1" variant="h5" fontWeight={600}>
            Create New Engagement
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Set up a new client engagement
          </Typography>
        </Box>

        <Box component="form" onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id="clientName"
                name="clientName"
                label="Client Name"
                autoFocus
                autoComplete="off"
                value={data.clientName}
                onChange={onChange}
                error={!!errors.clientName}
                helperText={errors.clientName}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="consultantName"
                name="consultantName"
                label="Consultant Name"
                autoComplete="off"
                disabled
                value={`${user.firstName} ${user.lastName}`}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="hourlyRateUsd"
                name="hourlyRateUsd"
                label="Hourly Rate (USD)"
                value={data.hourlyRateUsd}
                autoComplete="off"
                onChange={onChange}
                error={!!errors.hourlyRateUsd}
                helperText={errors.hourlyRateUsd}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="numberOfHoursPerWeek"
                name="numberOfHoursPerWeek"
                label="Hours Per Week"
                value={data.numberOfHoursPerWeek}
                autoComplete="off"
                onChange={onChange}
                error={!!errors.numberOfHoursPerWeek}
                helperText={errors.numberOfHoursPerWeek}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" sx={{ width: "100%" }}>
                <FormLabel component="legend" sx={{ mb: 1 }}>
                  Tracking Type
                </FormLabel>
                <RadioGroup
                  aria-label="trackingType"
                  name="trackingType"
                  value={data.trackingType}
                  onChange={onChange}
                >
                  <FormControlLabel
                    value="hourly-two-timesheets-per-month"
                    control={<Radio size="small" />}
                    label="Hourly, two timesheets per month (1st and 16th)"
                  />
                  <FormControlLabel
                    value="hourly"
                    control={<Radio size="small" />}
                    label="Hourly"
                  />
                  <FormControlLabel
                    value="daysOff"
                    control={<Radio size="small" />}
                    label="Days Off"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.startDate}>
                <FormLabel sx={{ mb: 1 }}>Start Date *</FormLabel>
                <DatePicker
                  selected={data.startDate}
                  onChange={handleStartDateChange}
                  customInput={<TextField fullWidth size="small" />}
                />
                {errors.startDate && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.startDate}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="shortDescription"
                name="shortDescription"
                label="Short Description"
                autoComplete="off"
                value={data.shortDescription}
                onChange={onChange}
                error={!!errors.shortDescription}
                helperText={errors.shortDescription}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description"
                autoComplete="off"
                multiline
                rows={4}
                value={data.description}
                onChange={onChange}
                error={!!errors.description}
                helperText={errors.description}
                fullWidth
                required
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{
              mt: 3,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            Create Engagement
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

const mapStateToProps = (storeState: StoreState) => ({
  isRegisterFailed: storeState.authentication.registerFailed,
});

export default connect(mapStateToProps, { createEngagement })(EngagementsCreate);
